import { useAsyncAction } from "../../hooks/useAsyncAction";
import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowRight, LoaderCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@components/ui/button";
import { getConnectionSyncStatus, triggerConnectionSync, updateConnectionSyncSchedule } from "../../lib/integrations";
import { ChoiceSelect } from "@components/ui/choice-select";
import { Switch } from "@components/ui/switch";

type Status = Awaited<ReturnType<typeof getConnectionSyncStatus>>;
type ObligationOutcomes = NonNullable<NonNullable<Status["latestRun"]>["obligationOutcomes"]>;

function obligationOutcomeTitle(count: number) {
  if (count === 0) return "No obligations changed in this sync.";
  return count === 1 ? "1 obligation is ready to review." : `${count} obligations are ready to review.`;
}

function sourceItemCopy(count: number) {
  return count === 1 ? "1 source item did not produce an obligation." : `${count} source items did not produce an obligation.`;
}

function evaluationFailureCopy(count: number) {
  const items = count === 1 ? "item" : "items";
  return `IO Spoke could not finish evaluating ${count} ${items}. Sync again to retry.`;
}

function syncRunCopy(status: Status) {
  const run = status.latestRun;
  if (!run) return null;
  if (status.freshness === "failed") return `Last sync failed: ${run.errorMessage ?? "Try syncing again."}`;
  if (run.status === "retrying") return "Sync interrupted. IO Spoke is trying again.";
  const items = run.recordsUpserted === 1 ? "item" : "items";
  return `${run.recordsUpserted} source ${items} updated`;
}

function SyncHealthNotice({ status }: { status: Status | null }) {
  if (status?.health === "intervention_required") {
    return <span className="text-xs font-medium text-destructive">Needs attention after {status.consecutiveFailures} failures</span>;
  }
  if (status?.health === "degraded") {
    return <span className="text-xs font-medium text-amber-600">Sync delayed · IO Spoke is trying again</span>;
  }
  return null;
}

function SyncOutcome({ outcomes, onOpenObligations }: { outcomes: ObligationOutcomes; onOpenObligations: () => void }) {
  const ready = outcomes.created + outcomes.updated + outcomes.unchanged;
  return (
    <div className="rounded-md border bg-background p-3" role="status" aria-live="polite">
      <p className="text-sm font-medium">{obligationOutcomeTitle(ready)}</p>
      {outcomes.skipped > 0 ? (
        <p className="mt-1 text-xs text-muted-foreground">{sourceItemCopy(outcomes.skipped)}</p>
      ) : null}
      {outcomes.failures > 0 ? (
        <p className="mt-1 text-xs font-medium text-destructive">{evaluationFailureCopy(outcomes.failures)}</p>
      ) : null}
      {ready > 0 ? (
        <Button className="mt-3 min-h-11" size="sm" onClick={onOpenObligations}>
          Review obligations <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
        </Button>
      ) : null}
    </div>
  );
}

export function IntegrationSyncControl({
  connectionId,
  providerName = "Jira",
  onOpenObligations,
}: {
  connectionId: string;
  providerName?: string;
  onOpenObligations: () => void;
}) {
  const [status, setStatus] = useState<Status | null>(null);
  const [loading, setLoading] = useState(true);
  const scheduleAction = useAsyncAction();
  const syncAction = useAsyncAction();
  const retryAction = useAsyncAction();
  const savingSchedule = scheduleAction.pending;
  const [statusError, setStatusError] = useState<string | null>(null);
  const mounted = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const next = await getConnectionSyncStatus(connectionId);
      if (mounted.current) {
        setStatus(next);
        setStatusError(null);
      }
      return next;
    } catch (error) {
      if (mounted.current) setStatusError(error instanceof Error ? error.message : "Unable to load synchronization status.");
      throw error;
    }
  }, [connectionId]);

  useEffect(() => {
    mounted.current = true;
    void refresh().catch(() => undefined).finally(() => { if (mounted.current) setLoading(false); });
    return () => { mounted.current = false; };
  }, [refresh]);

  useEffect(() => {
    if (status?.freshness !== "syncing") return;
    const timer = window.setInterval(() => void refresh().catch(() => undefined), 1_000);
    return () => window.clearInterval(timer);
  }, [refresh, status?.freshness]);

  const sync = () => syncAction.run(async () => {
    try {
      await triggerConnectionSync(connectionId);
      const next = await refresh();
      if (next.freshness !== "syncing") toast.success(`${providerName} sync complete.`);
    } catch (error) { toast.error(error instanceof Error ? error.message : `Unable to synchronize ${providerName}.`); }
  });

  const syncing = loading || syncAction.pending || status?.freshness === "syncing";
  const saveSchedule = (enabled: boolean, intervalMinutes = status?.schedule.intervalMinutes ?? 60) => scheduleAction.run(async () => {
    try { await updateConnectionSyncSchedule(connectionId, { enabled, intervalMinutes }); await refresh(); toast.success(enabled ? `${providerName} will sync automatically.` : `${providerName} automatic sync paused.`); }
    catch (error) { toast.error(error instanceof Error ? error.message : `Unable to update ${providerName} schedule.`); }
  });
  const nextSync = status?.schedule.nextSyncAt ? new Date(status.schedule.nextSyncAt).toLocaleString() : null;
  return <div className="w-full space-y-3 rounded-lg border bg-muted/20 p-3">
    {statusError ? (
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3" role="alert">
        <p className="text-sm text-destructive">{statusError}</p>
        <Button variant="outline" size="sm" className="min-h-11" onClick={() => void retryAction.run(async () => { await refresh().catch(() => undefined); })} loading={retryAction.pending} loadingLabel="Refreshing…">Try again</Button>
      </div>
    ) : null}
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex min-h-11 items-center gap-2">
        <Switch aria-label={`Automatically synchronize ${providerName}`} checked={status?.schedule.enabled ?? false}
          disabled={!status || savingSchedule} aria-busy={savingSchedule} onCheckedChange={(checked) => void saveSchedule(checked)}/>
        <div><p className="text-sm font-medium" role="status">{savingSchedule ? <><LoaderCircle className="mr-1 inline h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />Saving schedule…</> : "Automatic sync"}</p><p className="text-xs text-muted-foreground">{status?.schedule.enabled && nextSync ? `Next run ${nextSync}` : "Manual sync remains available"}</p></div>
      </div>
      <ChoiceSelect aria-label={`${providerName} synchronization frequency`} className="w-40" disabled={!status?.schedule.enabled || savingSchedule} aria-busy={savingSchedule}
        value={String(status?.schedule.intervalMinutes ?? 60)} onValueChange={(value) => void saveSchedule(true, Number(value))} options={[{ value: "15", label: "Every 15 minutes" }, { value: "60", label: "Every hour" }, { value: "360", label: "Every 6 hours" }, { value: "1440", label: "Every day" }, { value: "10080", label: "Every week" }]} />
    </div>
    <div className="flex flex-wrap items-center gap-2">
      <Button variant="outline" onClick={sync} disabled={syncing} loading={syncing} loadingLabel={`Syncing ${providerName}…`}><RefreshCw className="mr-2 h-4 w-4"/>Sync now</Button>
      {status?.latestRun ? <span className="text-xs text-muted-foreground" aria-live="polite">{syncRunCopy(status)}</span> : null}
      <SyncHealthNotice status={status} />
    </div>
    {status?.latestRun?.obligationOutcomes ? <SyncOutcome outcomes={status.latestRun.obligationOutcomes} onOpenObligations={onOpenObligations} /> : null}
  </div>;
}
